<template>
  <div class="subscription-tab-wrapper">
    <div class="subscription-header mb-8">
      <h1 class="font-weight-medium">{{ $t('global.subscription.mySubscriptions') }}</h1>
    </div>

    <div class="subscription-tab-content">
      <div
        v-if="loadingSubscriptionData"
        class="d-flex justify-center mt-8 items-center align-center subscription-card"
      >
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else class="subscription-card">
        <!-- Pending Transaction State (only for device admin) -->
        <div v-if="hasPendingTransaction && isDeviceAdmin" class="pending-transaction">
          <div class="subscription-icon">
            <v-icon size="32" color="warning">mdi-credit-card-clock</v-icon>
          </div>

          <h5>{{ $t('global.subscription.mobileConnectionM2M') }}</h5>

          <div class="status-badge pending">
            {{ $t('global.transaction.paymentPending') }}
          </div>

          <!-- Show subscription dates if available -->
          <div
            v-if="
              subscriptionData && (subscriptionData.startDate || subscriptionData.expirationDate)
            "
            class="subscription-dates"
          >
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionFrom') }}</span>
              <p>{{ formattedStartDate }}</p>
            </div>
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionExpires') }}</span>
              <p>{{ formattedExpirationDate }}</p>
            </div>
          </div>

          <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
            $t('global.moreInfoAboutSubscriptionServices')
          }}</a>
          <p class="pending-amount mt-3">
            <strong>{{ $t('global.amount') }}: </strong>
            {{ (pendingTransactionData.amountInCents / 100).toFixed(2) }}
            {{ pendingTransactionData.currency }}
          </p>

          <div class="subscription-info-actions">
            <button
              class="v-btn primary v-size--default renew-button"
              @click="completePendingPayment"
            >
              <v-icon left>mdi-credit-card</v-icon>
              {{ $t('global.transaction.completePayment') }}
            </button>
          </div>
        </div>

        <!-- No Subscription State -->
        <div v-else-if="isSubscriptionNotDefined">
          <h5>{{ $t('global.subscription.noSubscriptionRequired') }}</h5>
          <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
            $t('global.moreInfoAboutSubscriptionServices')
          }}</a>
        </div>

        <!-- Active/Expired Subscription State -->
        <div
          v-else-if="
            subscriptionData && (subscriptionData.startDate || subscriptionData.expirationDate)
          "
        >
          <!-- Icon -->
          <div class="subscription-icon">
            <v-icon size="32" color="primary">mdi-antenna</v-icon>
          </div>

          <h5>{{ $t('global.subscription.mobileConnectionM2M') }}</h5>
          <!-- Status Badge -->
          <div
            class="status-badge"
            :class="{
              expired: isSubscriptionExpired,
              unpaid: isSubscriptionUnpaid,
              'expiring-soon':
                !isSubscriptionExpired &&
                !isSubscriptionUnpaid &&
                !isMoreThan30DaysLater(subscriptionData?.expirationDate),
              active:
                !isSubscriptionExpired &&
                !isSubscriptionUnpaid &&
                isMoreThan30DaysLater(subscriptionData?.expirationDate),
            }"
          >
            {{ subscriptionStatusLabel }}
          </div>

          <div class="subscription-dates">
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionFrom') }}</span>
              <p>{{ formattedStartDate }}</p>
            </div>
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionExpires') }}</span>
              <p>{{ formattedExpirationDate }}</p>
            </div>
          </div>

          <div class="subscription-info-actions">
            <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
              $t('global.moreInfoAboutSubscriptionServices')
            }}</a>
            <button
              v-if="isDeviceAdmin && billingInfo?.paymentsManagedByEledio"
              :disabled="isMoreThan30DaysLater(subscriptionData?.expirationDate)"
              class="v-btn primary v-size--default renew-button"
              @click="openDialog"
            >
              {{ $t('global.subscription.renew') }}
            </button>
          </div>
        </div>

        <!-- Fallback for edge cases -->
        <div v-else>
          <h5>{{ $t('global.subscription.noSubscriptionRequired') }}</h5>
          <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
            $t('global.moreInfoAboutSubscriptionServices')
          }}</a>
        </div>
      </div>

      <!-- Billing Information Display -->
      <div
        v-if="isDeviceAdmin && billingInfo?.billingInfoManagedByEledio"
        class="billing-info-card"
      >
        <div class="billing-header">
          <h2 class="billing-title">{{ $t('global.billing.billingInformation') }}</h2>
          <v-btn v-if="!editMode" icon small @click="enableEditMode">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>

        <div v-if="loadingBillingInfo" class="d-flex justify-center mt-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else>
          <!-- View Mode -->
          <div v-if="!editMode && billingInfo" class="billing-details">
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.firstName') }}</span>
                <span class="value">{{ billingInfo.firstName }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.lastName') }}</span>
                <span class="value">{{ billingInfo.lastName ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.company') }}</span>
                <span class="value">{{ billingInfo.company ?? '-' }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.email') }}</span>
                <span class="value">{{ billingInfo.email ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.phone') }}</span>
                <span class="value">{{ billingInfo.phone || '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.country') }}</span>
                <span class="value">{{ displayCountry }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.street') }}</span>
                <span class="value">{{ billingInfo.street ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.streetNumber') }}</span>
                <span class="value">{{ billingInfo.streetNumber ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.apartmentNumber') }}</span>
                <span class="value">{{ billingInfo.apartmentNumber ?? '-' }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.zipCode') }}</span>
                <span class="value">{{ billingInfo.zipCode ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.city') }}</span>
                <span class="value">{{ billingInfo.city ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.invoiceLang') }}</span>
                <span class="value">{{
                  allowedLanguages.filter((lang) => lang.value === billingInfo.invoiceLang)[0].text
                }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.vat') }}</span>
                <span class="value">{{ billingInfo.vat ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.companyId') }}</span>
                <span class="value">{{ billingInfo.companyId ?? '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-if="editMode" class="billing-form">
            <v-form ref="billingForm" class="billing-edit-form" v-model="isFormValid">
              <div class="billing-form-content">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.firstName"
                      :label="'* ' + $t('global.billing.firstName')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.firstName
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.lastName"
                      :label="'* ' + $t('global.billing.lastName')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.lastName
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.company"
                      :label="$t('global.billing.company')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.email"
                      :label="'* ' + $t('global.billing.email')"
                      :rules="[rules.email, rules.required]"
                      dense
                      :error-messages="getEmailErrors"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-row no-gutters>
                      <v-col cols="5" class="pr-2">
                        <v-text-field
                          ref="billingPhonePrefix"
                          v-model="editedBilling.phonePrefix"
                          :label="$t('global.billing.phonePrefix')"
                          :rules="[rules.phonePrefix]"
                          dense
                          @focus="showAllErrors"
                          placeholder="+420"
                          @input="onBillingPhonePrefixChange"
                        />
                      </v-col>
                      <v-col cols="7">
                        <v-text-field
                          ref="billingPhoneNumber"
                          v-model="editedBilling.phoneNumber"
                          :label="$t('global.billing.phone')"
                          :rules="[rules.phoneNumber]"
                          dense
                          @focus="showAllErrors"
                          placeholder="602123456"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="editedBilling.country"
                      :items="countryItems"
                      item-text="text"
                      item-value="value"
                      :label="'* ' + $t('global.billing.country')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.country
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.street"
                      :label="$t('global.billing.street')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.streetNumber"
                      :label="$t('global.billing.streetNumber')"
                      :rules="[rules.numeric]"
                      dense
                      :error-messages="getNumericError('streetNumber')"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.apartmentNumber"
                      :label="$t('global.billing.apartmentNumber')"
                      :rules="[rules.numeric]"
                      dense
                      :error-messages="getNumericError('apartmentNumber')"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.zipCode"
                      :label="$t('global.billing.zipCode')"
                      dense
                      :error-messages="getZipCodeErrors"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.city"
                      :label="$t('global.billing.city')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      v-model="editedBilling.invoiceLang"
                      :label="$t('global.billing.invoiceLang')"
                      dense
                      :items="allowedLanguages"
                      item-text="text"
                      item-value="value"
                      @focus="showAllErrors"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="editedBilling.vat"
                      :label="$t('global.billing.vat')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="editedBilling.companyId"
                      :label="$t('global.billing.companyId')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <div v-if="isDeviceAdmin" class="billing-form-actions">
                <v-btn
                  text
                  color="grey"
                  class="mr-2"
                  @click="cancelEdit"
                  :disabled="updatingBilling"
                >
                  {{ $t('global.cancel') }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="saveBillingInfo"
                  :loading="updatingBilling"
                  :disabled="updatingBilling || !isFormValid"
                >
                  {{ $t('global.save') }}
                </v-btn>
              </div>
            </v-form>
          </div>
        </div>

        <v-alert type="error" v-if="error">
          {{ error.message }}
        </v-alert>
      </div>
    </div>
    <SubscriptionRenewDialog :value="dialogOpen" :onClose="closeDialog" :device="device" />

    <!-- Payment Success Dialog -->
    <v-dialog v-model="showSuccessDialog" max-width="500">
      <v-card>
        <v-card-text class="text-center pa-8">
          <v-icon color="success" size="80" class="mb-4">mdi-check-circle</v-icon>
          <h2 class="text-h5 font-weight-medium mb-4">
            {{ $t('global.transaction.successTitle') }}
          </h2>
          <p class="text-body-1 text-medium-emphasis">
            {{ $t('global.transaction.successMessage') }}
          </p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="primary" large @click="showSuccessDialog = false">
            {{ $t('global.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SubscriptionRenewDialog from '../../Subscription/SubscriptionRenewDialog.vue'
import { subscriptionStatus } from '@/graphql/query/subscriptionStatus'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import { pendingTransaction } from '@/graphql/query/pendingTransaction'
import { billingInfoUpdate } from '@/graphql/mutations/billingInfoUpdate'
import { countryCodesWithName } from '../../../config/countryCodes'
import {
  isPhoneNumber,
  splitE164,
  composeE164,
  normalizePrefix,
} from '@/utils/phoneNumberValidator'

export default {
  name: 'SubscriptionOverviewTab',
  components: {
    SubscriptionRenewDialog,
  },
  props: {
    tabConfig: Object,
    device: {
      type: Object,
      required: true,
    },
    deviceDetailWrapper: HTMLDivElement,
    active: Boolean,
  },
  data() {
    return {
      dialogOpen: false,
      showSuccessDialog: false,
      loadingBillingInfo: true,
      loadingSubscriptionData: true,
      billingInfo: null,
      pendingTransactionData: null,
      error: null,
      editMode: false,
      editedBilling: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phone: '',
        country: '',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        vat: '',
        companyId: '',
        invoiceLang: 'cze', // Default language for invoices
      },
      rules: {
        required: (value) => !!value || this.$t('global.formValidation.required'),
        email: (value) => {
          if (!value) return true // Allow empty email
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || this.$t('global.formValidation.notEmail')
        },
        phonePrefix: (value) => {
          if (!this.editedBilling.phoneNumber && !value) return true
          if (!value) return this.$t('global.formValidation.required')
          return /^\+[1-9]\d{0,3}$/.test(value) || this.$t('global.formValidation.notPhoneNumber')
        },
        phoneNumber: (value) => {
          if (!this.editedBilling.phonePrefix && !value) return true
          if (!value) return this.$t('global.formValidation.required')
          const full = (this.editedBilling.phonePrefix || '') + String(value).replace(/\s+/g, '')
          return isPhoneNumber(full) || this.$t('global.formValidation.notPhoneNumber')
        },
        numeric: (value) => {
          if (!value) return true // Allow empty numeric fields
          const pattern = /^\d+$/
          return pattern.test(value) || this.$t('global.formValidation.notNumeric')
        },
      },
      countryItems: Object.entries(countryCodesWithName).map(([code, name]) => ({
        text: name,
        value: code,
      })),
      updatingBilling: false,
      isFormValid: false,
      showErrors: false,
      formErrors: {
        firstName: [],
        lastName: [],
        email: [],
        phoneNumber: [],
        country: [],
        street: [],
        streetNumber: [],
        apartmentNumber: [],
        zipCode: [],
        city: [],
      },
    }
  },
  computed: {
    isDeviceAdmin() {
      return this.device?.userAccess?.type === 'DEVICE_ADMIN'
    },
    allowedLanguages() {
      return [
        {
          text: this.$t('global.lang.cs'),
          value: 'cze',
        },
        {
          text: this.$t('global.lang.en'),
          value: 'eng',
        },
        {
          text: this.$t('global.lang.de'),
          value: 'deu',
        },
        {
          text: this.$t('global.lang.pl'),
          value: 'pol',
        },
      ]
    },
    isSubscriptionValid() {
      return this.subscriptionData?.subscriptionStatus === 'valid'
    },
    isSubscriptionExpired() {
      return this.subscriptionData?.subscriptionStatus === 'expired'
    },
    isSubscriptionUnpaid() {
      return (
        this.subscriptionData?.subscriptionStatus === 'unpaid' ||
        this.subscriptionData?.subscriptionStatus === 'blocked'
      )
    },
    isSubscriptionNotDefined() {
      return this.subscriptionData?.subscriptionStatus === 'not_defined'
    },
    hasPendingTransaction() {
      return !!this.pendingTransactionData
    },
    formattedStartDate() {
      return this.subscriptionData?.startDate
        ? this.formatDate(this.subscriptionData.startDate)
        : '-'
    },
    formattedExpirationDate() {
      return this.subscriptionData?.expirationDate
        ? this.formatExpirationDate(this.subscriptionData.expirationDate)
        : '-'
    },
    localizedLink() {
      return `/business-info`
    },
    displayCountry() {
      if (!this.billingInfo?.country) return '-'
      return countryCodesWithName[this.billingInfo.country] || this.billingInfo.country
    },
    getEmailErrors() {
      if (!this.showErrors) return []
      const errors = []
      if (!this.editedBilling.email) {
        errors.push(this.$t('global.formValidation.required'))
      } else if (!this.rules.email(this.editedBilling.email)) {
        errors.push(this.$t('global.formValidation.notEmail'))
      }
      return errors
    },
    getZipCodeErrors() {
      if (!this.showErrors || !this.editedBilling.zipCode) return []
      if (!this.rules.numeric(this.editedBilling.zipCode)) {
        return [this.$t('global.formValidation.notNumeric')]
      }
      return []
    },
    daysUntilExpiration() {
      if (!this.subscriptionData?.expirationDate) return 0
      const now = new Date()
      const expirationDate = new Date(parseInt(this.subscriptionData.expirationDate))
      const diffTime = expirationDate - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      // For expired subscriptions, we want the absolute value for display
      return this.isSubscriptionExpired ? Math.abs(diffDays) : diffDays
    },
    subscriptionStatusLabel() {
      if (
        this.isSubscriptionUnpaid ||
        this.subscriptionData?.subscriptionStatus === 'reactivation_pending'
      ) {
        // Use generic status translation map (VALID/SOON_EXPIRING/EXPIRED/UNPAID)
        return this.$t(
          'global.subscriptionStatus.' + this.subscriptionData?.subscriptionStatus?.toUpperCase(),
        )
      }
      if (this.isSubscriptionExpired) {
        return this.$t('global.subscription.subscriptionExpired', {
          days: this.daysUntilExpiration,
        })
      }
      if (!this.isMoreThan30DaysLater(this.subscriptionData?.expirationDate)) {
        return this.$t('global.subscription.subscriptionWillEndSoon', {
          days: this.daysUntilExpiration,
        })
      }
      return this.$t('global.subscription.activeSubscription')
    },
  },
  apollo: {
    subscriptionData: {
      query: subscriptionStatus,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      update(data) {
        this.loadingSubscriptionData = false
        return {
          subscriptionStatus: data.subscriptionStatus.subscriptionStatus.toLowerCase(),
          expirationDate: data.subscriptionStatus.expirationDate,
          startDate: data.subscriptionStatus.startDate,
        }
      },
      error(error) {
        this.loadingSubscriptionData = false
        console.error('Error fetching subscription status:', error)
        this.$toast.error(this.$t('global.errorFetchingData'))
      },
      loadingKey: 'loading',
    },
    billingInfo: {
      query: deviceBillingInfo,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      skip() {
        return !this.isDeviceAdmin
      },
      loadingKey: 'loading',
      update(data) {
        this.loadingBillingInfo = false
        if (data?.billingInfo) {
          this.billingInfo = data.billingInfo
        }
        return data?.billingInfo
      },
      error(error) {
        this.loadingBillingInfo = false
        this.error = error
      },
    },
    pendingTransactionData: {
      query: pendingTransaction,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      skip() {
        return !this.isDeviceAdmin
      },
      loadingKey: 'loading',
      update(data) {
        return data?.pendingTransaction
      },
      error(error) {
        // It's okay if no pending transaction exists
        console.log('No pending transaction found:', error)
      },
    },
  },
  mounted() {
    // Check if we're coming back from a successful payment
    if (this.$route.query.paymentSuccess === 'true') {
      this.showSuccessDialog = true
      // Remove the query parameter to clean up the URL
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: {},
      })
    }
  },
  methods: {
    closeDialog() {
      this.dialogOpen = false
    },
    openDialog() {
      this.dialogOpen = true
    },
    completePendingPayment() {
      if (this.pendingTransactionData?.paymentUrl) {
        window.location.href = this.pendingTransactionData.paymentUrl
      }
    },
    isMoreThan30DaysLater(timestamp) {
      const now = new Date()
      const futureDate = new Date()
      futureDate.setDate(now.getDate() + 30)
      return timestamp > futureDate.getTime()
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      try {
        const date = new Date(parseInt(timestamp))
        if (isNaN(date.getTime())) return '-'
        return `${String(date.getUTCDate()).padStart(2, '0')}.${String(
          date.getUTCMonth() + 1,
        ).padStart(2, '0')}.${date.getUTCFullYear()}`
      } catch (error) {
        console.error('Error formatting date:', error)
        return '-'
      }
    },
    formatExpirationDate(timestamp) {
      if (!timestamp) return '-'
      try {
        const date = new Date(parseInt(timestamp))
        if (isNaN(date.getTime())) return '-'
        // Use UTC methods and don't subtract day to match admin billing display
        return `${String(date.getUTCDate()).padStart(2, '0')}.${String(
          date.getUTCMonth() + 1,
        ).padStart(2, '0')}.${date.getUTCFullYear()}`
      } catch (error) {
        console.error('Error formatting expiration date:', error)
        return '-'
      }
    },
    showAllErrors() {
      this.showErrors = true
    },
    getNumericError(field) {
      if (!this.showErrors || !this.editedBilling[field]) return []
      return this.rules.numeric(this.editedBilling[field])
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    enableEditMode() {
      this.editMode = true
      const split = splitE164(this.billingInfo?.phone || '')
      this.editedBilling = {
        ...this.billingInfo,
        phone: undefined,
        phonePrefix: split.prefix,
        phoneNumber: split.national,
        invoiceLang: this.billingInfo?.invoiceLang || 'cze', // Default to Czech if not set
      }
      this.showErrors = false
    },
    cancelEdit() {
      this.editMode = false
      this.showErrors = false
    },
    async saveBillingInfo() {
      const isValid = await this.$refs.billingForm.validate()

      if (!isValid) {
        this.$toast.error(this.$t('global.fillRequiredFields'))
        return
      }

      this.updatingBilling = true

      try {
        // Compose E.164 phone from prefix + national (optional if both empty)
        const phone = composeE164(this.editedBilling.phonePrefix, this.editedBilling.phoneNumber)

        // Prepare the input for the mutation
        const billingInfoInput = {
          firstName: this.editedBilling.firstName,
          lastName: this.editedBilling.lastName,
          company: this.editedBilling.company,
          phone: phone,
          country: this.editedBilling.country,
          street: this.editedBilling.street,
          streetNumber: this.editedBilling.streetNumber,
          apartmentNumber: this.editedBilling.apartmentNumber,
          zipCode: this.editedBilling.zipCode,
          city: this.editedBilling.city,
          email: this.editedBilling.email,
          vat: this.editedBilling.vat,
          companyId: this.editedBilling.companyId,
          invoiceLang: this.editedBilling.invoiceLang,
        }

        // Call the mutation
        const { data } = await this.$apollo.mutate({
          mutation: billingInfoUpdate,
          variables: {
            deviceId: this.device.id,
            billingInfo: billingInfoInput,
          },
        })

        if (data.billingInfoUpdate?.success) {
          this.$toast.success(this.$t('global.dataUpdateSuccess'))
          this.editMode = false

          // Refresh billing info data by refetching the query
          this.$apollo.queries.billingInfo.refetch()
        } else {
          this.$toast.error(this.$t('global.billing.updateError'))
        }
      } catch (error) {
        console.error('Error updating billing info:', error)
        this.$toast.error(error.message || this.$t('global.billing.updateError'))
      } finally {
        this.updatingBilling = false
      }
    },
    onBillingPhonePrefixChange() {
      this.editedBilling.phonePrefix = normalizePrefix(this.editedBilling.phonePrefix)
      if (this.$refs.billingPhoneNumber && this.$refs.billingPhoneNumber.validate) {
        this.$refs.billingPhoneNumber.validate()
      }
    },
  },
  watch: {
    editedBilling: {
      deep: true,
      handler() {
        if (this.showErrors) {
          this.validateAllFields()
        }
      },
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@/assets/less/variables.less';

.subscription-header {
  border-radius: 4px;
  position: relative;

  h1 {
    margin: 0;
    padding: 0.5rem 0;
    color: #333;
    font-size: 14px;
  }
}

.subscription-tab-content {
  display: grid;
  grid-template-columns: 2fr 4fr;
  flex-direction: row;
  gap: 24px;

  @media (max-width: 1200px) {
    display: flex;
    flex-direction: column;
  }
}

.subscription-tab-wrapper {
  border: 1px solid #e5e5e5;
  padding: 8px 24px 32px 24px;
  h1 {
    font-size: 24px;
  }

  h2 {
    font-weight: 400;
    font-size: 14px;
    color: #8089ac;
  }
  h5 {
    font-size: 1rem;
    margin-bottom: 8px;
  }
  .subscription-info-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .subscription-service-link {
    font-size: 14px;
    color: #8089ac;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  p {
    font-size: 0.875rem;
    margin-bottom: 0;
  }

  .renew-button {
    width: 100%;
    margin-top: 16px;
  }

  .renew-button:disabled {
    background-color: #dfe4e9 !important;
    color: #b3bfcb;
  }

  .subscription-info {
    display: flex;
    width: fit-content;
    margin-top: 20px;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: 100%;
    padding: 24px;
    height: 300px;
    background-color: rgba(211, 241, 255, 0.5);
  }

  .billing-info-card {
    grid-column-start: 1;
    grid-column-end: 6;
    flex: 1;
    background: white;
    border-radius: 0;
    padding: 24px;
    border: 1px solid #e5e5e5;
    min-width: 0;
    width: 100%;
    min-height: 372px;
    display: flex;
    flex-direction: column;
  }

  .billing-title {
    color: #333;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 24px;
  }

  .billing-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
  }

  .billing-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 24px;

    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .billing-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .label {
    color: #8089ac;
    font-size: 14px;
  }

  .value {
    color: #333;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-alert {
    margin-top: 16px;
  }

  .billing-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .billing-form {
    margin-top: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .billing-form .v-form {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .billing-form .v-row {
    margin-bottom: 16px;
  }

  .billing-form .v-col {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .billing-form .d-flex.justify-end {
    margin-top: auto;
    padding-top: 24px;
  }

  /* Ensure labels have consistent appearance with view mode */
  .v-text-field .v-label,
  .v-select .v-label {
    color: #8089ac;
    font-size: 14px;
  }

  /* Add a bit more height to the inputs */
  .v-input--dense .v-input__slot {
    min-height: 36px !important;
  }

  /* Ensure consistent spacing between form fields */
  .v-text-field.v-text-field--enclosed .v-input__slot,
  .v-select.v-select--enclosed .v-input__slot {
    margin-bottom: 4px;
  }

  .billing-edit-form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 300px; /* Match the height of view mode */
  }

  .billing-form-content {
    flex: 1;
  }

  .billing-form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 24px;
    margin-top: auto;
  }
}

.subscription-card {
  flex: 0 0 300px;
  background: white;
  border-radius: 0;
  padding: 24px;
  text-align: center;
  border: 1px solid #e5e5e5;
  align-self: flex-start;
  min-height: 372px;
  width: 100%;
}

.subscription-icon {
  background: #f5f5f5;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  background: #27ae60; /* Green for active */
  color: white;
  font-size: 14px;
  margin-bottom: 16px;
}

.status-badge.expired {
  background: #ff4d4f; /* Red for expired */
}

.status-badge.unpaid {
  background: #ff4d4f; /* Red for unpaid */
}

.status-badge.pending {
  background: #ff9800; /* Orange for pending */
}

.pending-transaction {
  text-align: center;
}

.pending-amount {
  font-size: 16px;
  color: #333;
}

.status-badge.expiring-soon {
  background: #ffe7e7; /* Light red background */
  color: #ff4d4f; /* Red text */
}

.subscription-dates {
  text-align: left;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.date-item {
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.date-item span {
  font-size: 14px;
  color: #8089ac;
}

.date-item p {
  font-size: 14px;
  color: #454d57;
}

.subscription-info-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subscription-service-link {
  color: var(--v-primary-base);
  text-decoration: none;
}

.renew-button {
  width: 100%;
}
</style>
